import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { formatCurrency, formatHours } from '@/lib/budget-utils';
import type { BudgetData } from '@/types/budget.types';

interface BudgetRevisionPanelProps {
  changePercentage: number;
  requiresApproval: boolean;
  revisionReason: string;
  onRevisionReasonChange: (reason: string) => void;
  saveAsBaseline: boolean;
  onSaveAsBaselineChange: (value: boolean) => void;
  costBudgetChanges: any;
  salesBudgetChanges: any;
  resourceBudgetChanges: any;
  originalBudget: BudgetData;
}

export function BudgetRevisionPanel({
  changePercentage,
  requiresApproval,
  revisionReason,
  onRevisionReasonChange,
  saveAsBaseline,
  onSaveAsBaselineChange,
  costBudgetChanges,
  salesBudgetChanges,
  resourceBudgetChanges,
  originalBudget,
}: BudgetRevisionPanelProps) {
  const hasChanges = costBudgetChanges || salesBudgetChanges || resourceBudgetChanges;

  // Calculate change summary
  const getChangeSummary = () => {
    const summary: { type: string; field: string; from: string; to: string; change: string }[] = [];

    if (costBudgetChanges) {
      const originalTotal = originalBudget.costBudget?.totals?.budget || 0;
      const newTotal = costBudgetChanges.totals?.budget || 0;
      const change = newTotal - originalTotal;
      
      if (change !== 0) {
        summary.push({
          type: 'Cost Budget',
          field: 'Total Budget',
          from: formatCurrency(originalTotal),
          to: formatCurrency(newTotal),
          change: formatCurrency(Math.abs(change)),
        });
      }
    }

    if (salesBudgetChanges) {
      const originalTotal = originalBudget.salesBudget?.contractValue || 0;
      const newTotal = salesBudgetChanges.contractValue || 0;
      const change = newTotal - originalTotal;
      
      if (change !== 0) {
        summary.push({
          type: 'Sales Budget',
          field: 'Contract Value',
          from: formatCurrency(originalTotal),
          to: formatCurrency(newTotal),
          change: formatCurrency(Math.abs(change)),
        });
      }
    }

    if (resourceBudgetChanges) {
      const originalTotal = originalBudget.resourceBudget?.summary?.totalBudget || 0;
      const newTotal = resourceBudgetChanges.summary?.totalBudget || 0;
      const change = newTotal - originalTotal;
      
      if (change !== 0) {
        summary.push({
          type: 'Resource Budget',
          field: 'Total Hours',
          from: formatHours(originalTotal),
          to: formatHours(newTotal),
          change: formatHours(Math.abs(change)),
        });
      }
    }

    return summary;
  };

  const changeSummary = getChangeSummary();

  return (
    <div className="space-y-4">
      {/* Change Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Change Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {!hasChanges ? (
            <div className="text-center py-8 text-muted-foreground">
              No changes have been made yet.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <Info className="h-4 w-4 text-info-500" />
                <span>
                  Total budget change: <strong>{changePercentage.toFixed(1)}%</strong>
                </span>
              </div>

              {changeSummary.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3">Budget Type</th>
                        <th className="text-left p-3">Field</th>
                        <th className="text-right p-3">From</th>
                        <th className="text-right p-3">To</th>
                        <th className="text-right p-3">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changeSummary.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-3 font-medium">{item.type}</td>
                          <td className="p-3">{item.field}</td>
                          <td className="p-3 text-right text-muted-foreground">{item.from}</td>
                          <td className="p-3 text-right font-medium">{item.to}</td>
                          <td className="p-3 text-right">
                            <span className="text-warning-600">±{item.change}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approval Alert */}
      {requiresApproval && (
        <Alert variant="default" className="border-warning-500 bg-warning-50 dark:bg-warning-950">
          <AlertTriangle className="h-4 w-4 text-warning-600" />
          <AlertDescription className="text-warning-800 dark:text-warning-200">
            This change is <strong>{changePercentage.toFixed(1)}%</strong> of the current budget
            and will require approval from the Finance Manager before it takes effect.
          </AlertDescription>
        </Alert>
      )}

      {/* Revision Reason */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Reason for Change {requiresApproval && <span className="text-destructive">*</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="revision-reason">
              Please provide a clear explanation for this budget revision
              {requiresApproval && ' (required for changes >5%)'}
            </Label>
            <Textarea
              id="revision-reason"
              value={revisionReason}
              onChange={(e) => onRevisionReasonChange(e.target.value)}
              placeholder="e.g., Additional scope approved by client, Resource allocation adjustment, Phase extension..."
              rows={4}
              className={requiresApproval && !revisionReason.trim() ? 'border-destructive' : ''}
            />
            {requiresApproval && !revisionReason.trim() && (
              <p className="text-sm text-destructive">
                A reason is required for budget changes greater than 5%
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Baseline Option */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Revision Options</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start space-x-3">
            <Checkbox
              id="save-as-baseline"
              checked={saveAsBaseline}
              onCheckedChange={(checked) => onSaveAsBaselineChange(checked as boolean)}
            />
            <div className="space-y-1">
              <Label
                htmlFor="save-as-baseline"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Save as new baseline
              </Label>
              <p className="text-sm text-muted-foreground">
                Lock the current budget as a baseline revision. This creates a snapshot that can be
                used for comparison and reporting. Previous baselines will remain accessible in the
                revision history.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revision History Info */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          All budget changes are tracked and can be reviewed in the Budget History. You can compare
          any two revisions to see what changed over time.
        </AlertDescription>
      </Alert>
    </div>
  );
}
